import { useEffect, useState } from 'react'
import {
  IconDotsVertical, IconFileText, IconFolder, IconLayoutGrid, IconList, IconLock, IconNotebook, IconPlus, IconSearch,
  IconTrash, IconUsers,
} from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { projectApi } from '@/api/endpoints'
import { projectCan, useSystemCan } from '@/hooks/usePermissions'
import { errorMessage } from '@/api/client'
import type { Project, ProjectScope, Visibility } from '@/api/types'
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States'
import { TrashDrawer } from '@/components/common/TrashDrawer'
import { PageHeader } from '@/components/common/PageHeader'
import { Pager } from '@/components/common/Pager'
import { RoleBadge, VisibilityBadge } from '@/components/common/RoleBadge'
import { formatRelative } from '@/utils/format'
import { tr } from '@/i18n/tr'
import {
  Button, IconButton, Menu, MenuItem, Modal, Monogram, RadioCard, Segmented, TextField, Textarea,
  modals, notifications, useDebouncedValue, useDisclosure, useForm, useLocalStorage, useMediaQuery,
} from '@/ui'
import { projectColor } from '../../../packages/shared/src/tokens'
import './projects.css'

/** Kart ızgarası üç sütun; 24 kart tam sekiz sıra eder. */
const SAYFA_BOYU = 24

type Gorunum = 'grid' | 'list'

export function ProjectsPage() {
  const [q, setQ] = useState('')
  const [aranan] = useDebouncedValue(q.trim(), 250)
  const [scope, setScope] = useState<ProjectScope>('ALL')
  const [page, setPage] = useState(0)
  const [secilenGorunum, setGorunum] = useLocalStorage<Gorunum>({ key: 'arsivora.projeler.gorunum', defaultValue: 'grid' })
  // Dar ekranda tablo sığmaz; kartlar zaten tek sütun liste gibi okunur.
  const dar = useMediaQuery('(max-width: 899.98px)')
  const gorunum: Gorunum = dar ? 'grid' : secilenGorunum
  const [opened, { open, close }] = useDisclosure(false)
  const [copOpened, cop] = useDisclosure(false)
  const [params, setParams] = useSearchParams()
  const qc = useQueryClient()
  const canCreate = useSystemCan()('project.create')

  // Kenar menüdeki ve komut paletindeki "Yeni proje" buraya ?yeni=1 ile gelir.
  useEffect(() => {
    if (params.get('yeni') === '1') {
      if (canCreate) open()
      params.delete('yeni')
      setParams(params, { replace: true })
    }
  }, [params, setParams, open, canCreate])

  // Filtre değişince 3. sayfada kalmak boş liste demek olurdu.
  useEffect(() => { setPage(0) }, [aranan, scope])

  const query = useQuery({
    queryKey: ['projects', aranan, scope, page],
    queryFn: () => projectApi.list({ q: aranan || undefined, scope, page, size: SAYFA_BOYU }),
    // Üyelik/rol başka bir oturumda değişebilir (ör. biri bu kullanıcıyı projeye ekler).
    // Liste her açılışta ve sekmeye dönüşte tazelensin; aksi halde eklenen proje eski
    // (boş) listede görünmez ve "projeyi göremiyorum" sanılır.
    staleTime: 0,
    refetchOnWindowFocus: true,
  })
  // Kapsam sayıları (Tümü / Sahibi olduklarım / Paylaşılanlar): sayfa başına iki hafif istek.
  const sayi = (s: ProjectScope) => ({
    queryKey: ['projects-count', aranan, s],
    queryFn: () => projectApi.list({ q: aranan || undefined, scope: s, size: 1 }).then((p) => p.totalElements),
    staleTime: 0,
    refetchOnWindowFocus: true,
  })
  const tumu = useQuery(sayi('ALL'))
  const benim = useQuery(sayi('OWNED'))
  const paylasilan = useQuery(sayi('SHARED'))

  const remove = useMutation({
    mutationFn: (id: string) => projectApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] })
      qc.invalidateQueries({ queryKey: ['projects-count'] })
      notifications.show({ color: 'green', message: 'Proje çöp kutusuna taşındı.' })
    },
    onError: (e) => notifications.show({ color: 'red', message: errorMessage(e) }),
  })

  const confirmDelete = (p: Project) =>
    modals.openConfirmModal({
      title: `"${p.name}" silinsin mi?`,
      children: tr.project.deleteConfirm,
      labels: { confirm: tr.common.delete, cancel: tr.common.cancel },
      confirmProps: { color: 'red' },
      onConfirm: () => remove.mutate(p.id),
    })

  const subtitle = tumu.data !== undefined
    ? `${tumu.data} proje${paylasilan.data ? ` · ${paylasilan.data} tanesi sizinle paylaşıldı` : ''}`
    : undefined
  const sayac = (n: number | undefined) => <span className="nx-seg-count nx-nums">{n ?? ''}</span>
  const plus = <IconPlus size={16} stroke={2.2} aria-hidden="true" />
  const items = query.data?.content ?? []

  return (
    <>
      <PageHeader
        eyebrow="Çalışma alanı"
        title={tr.project.title}
        subtitle={subtitle}
        actions={
          <>
            <Button variant="secondary" leftIcon={<IconTrash size={17} stroke={1.8} aria-hidden="true" />} onClick={cop.open}>
              {tr.project.trash}
            </Button>
            {canCreate && <Button leftIcon={plus} onClick={open}>{tr.project.newProject}</Button>}
          </>
        }
      />

      <div className="nx-filterbar">
        <TextField className="nx-filterbar__search" aria-label="Projelerde ara" placeholder={tr.common.search} type="search"
          leftSection={<IconSearch size={18} stroke={1.8} aria-hidden="true" />} value={q}
          onChange={(e) => setQ(e.currentTarget.value)} />
        <Segmented aria-label="Sahiplik" value={scope} onChange={setScope}
          options={[
            { value: 'ALL', label: <>Tümü {sayac(tumu.data)}</> },
            { value: 'OWNED', label: <>Sahibi olduklarım {sayac(benim.data)}</> },
            { value: 'SHARED', label: <>Paylaşılanlar {sayac(paylasilan.data)}</> },
          ]} />
        <span className="nx-filterbar__spacer" />
        {!dar && <Segmented aria-label="Görünüm" value={gorunum} onChange={setGorunum}
          options={[
            { value: 'grid', label: <IconLayoutGrid size={17} stroke={1.8} aria-hidden="true" />, ariaLabel: 'Kart görünümü' },
            { value: 'list', label: <IconList size={17} stroke={1.8} aria-hidden="true" />, ariaLabel: 'Liste görünümü' },
          ]} />}
      </div>

      <TrashDrawer
        opened={copOpened}
        onClose={cop.close}
        title="Çöp kutusu · Projeler"
        queryKey={['projects-trash']}
        load={async () => {
          const page = await projectApi.trash()
          return page.content.map((p) => ({
            id: p.id,
            label: p.name,
            hint: p.deletedAt ? `Silindi · ${formatRelative(p.deletedAt)}` : undefined,
            leading: <Monogram name={p.name} color={projectColor(p.id)} size={38} radius={11} />,
          }))
        }}
        restore={(id) => projectApi.restore(id)}
        onRestored={() => {
          qc.invalidateQueries({ queryKey: ['projects'] })
          qc.invalidateQueries({ queryKey: ['projects-count'] })
        }}
      />

      {query.isLoading && <LoadingState />}
      {query.isError && <ErrorState error={query.error} onRetry={() => query.refetch()} />}

      {query.data && items.length === 0 && (
        aranan || scope !== 'ALL'
          ? <EmptyState icon={<IconSearch size={24} stroke={1.6} />} title="Eşleşen proje yok"
              hint={aranan ? 'Farklı bir kelime deneyin.' : 'Bu kapsamda proje yok.'} />
          : <EmptyState icon={<IconFolder size={24} stroke={1.6} />} title={tr.project.empty} hint={tr.project.emptyHint}
              action={canCreate && <Button leftIcon={plus} onClick={open}>{tr.project.newProject}</Button>} />
      )}

      {items.length > 0 && gorunum === 'grid' && (
        <div className="nx-project-grid">
          {items.map((p) => <ProjectCard key={p.id} project={p} onDelete={() => confirmDelete(p)} />)}
        </div>
      )}
      {items.length > 0 && gorunum === 'list' && <ProjectTable projects={items} onDelete={confirmDelete} />}

      <Pager page={page} totalPages={query.data?.totalPages} onChange={setPage} />

      <CreateProjectModal opened={opened} onClose={close} />
    </>
  )
}

function ProjectMenu({ project, onDelete }: { project: Project; onDelete: () => void }) {
  if (!projectCan(project)('project.delete')) return null
  return (
    <Menu align="end" width={180} trigger={
      <IconButton label="Proje işlemleri" size="sm" className="nx-pcard__menu"><IconDotsVertical size={18} stroke={2} /></IconButton>
    }>
      <MenuItem danger icon={<IconTrash size={16} />} onSelect={onDelete}>{tr.common.delete}</MenuItem>
    </Menu>
  )
}

/**
 * Tek bir proje kartı.
 *
 * <p>Baş harf karosu projenin kimliğinden türeyen sabit bir renk taşır. Amaç süs
 * değil tanıma: kullanıcı listedeki projeyi adını okumadan renginden seçebilsin.
 * Kartın tamamı tıklanır (ad bağlantısı kartı kaplar); işlem menüsü üstte kalır.
 */
function ProjectCard({ project: p, onDelete }: { project: Project; onDelete: () => void }) {
  const ic = { size: 14, stroke: 1.8, 'aria-hidden': true }
  return (
    <article className="nx-pcard nx-card-hover">
      <div className="nx-pcard__head">
        <Monogram name={p.name} color={projectColor(p.id)} size={46} radius={13} />
        <div className="nx-pcard__titles">
          <Link to={`/projeler/${p.id}`} className="nx-pcard__name">{p.name}</Link>
          <span className="nx-pcard__meta">{formatRelative(p.updatedAt)} · {p.ownerDisplayName}</span>
        </div>
        <ProjectMenu project={p} onDelete={onDelete} />
      </div>
      <p className={p.description ? 'nx-pcard__desc' : 'nx-pcard__desc nx-pcard__desc--empty'}>{p.description || 'Açıklama yok'}</p>
      <div className="nx-pcard__foot">
        <VisibilityBadge visibility={p.visibility} />
        {p.myRole && <RoleBadge role={p.myRole} name={p.roleNames?.[0]} />}
        <span className="nx-pcard__spacer" />
        <span className="nx-pcard__counts">
          {p.noteCount != null && <span title={`${p.noteCount} not`}><IconNotebook {...ic} /><span className="nx-nums">{p.noteCount}</span></span>}
          {p.fileCount != null && <span title={`${p.fileCount} dosya`}><IconFileText {...ic} /><span className="nx-nums">{p.fileCount}</span></span>}
          {p.visibility === 'SHARED' && <span title={`${p.memberCount} üye`}><IconUsers {...ic} /><span className="nx-nums">{p.memberCount}</span></span>}
        </span>
      </div>
    </article>
  )
}

function ProjectTable({ projects, onDelete }: { projects: Project[]; onDelete: (p: Project) => void }) {
  return (
    <div className="nx-panel nx-ptable-wrap">
      <table className="nx-ptable">
        <caption className="ui-visually-hidden">Projeler</caption>
        <thead>
          <tr><th scope="col">Proje</th><th scope="col">Görünürlük</th><th scope="col">Rolünüz</th><th scope="col">İçerik</th><th scope="col">Güncellenme</th><th scope="col"><span className="ui-visually-hidden">İşlemler</span></th></tr>
        </thead>
        <tbody>
          {projects.map((p) => (
            <tr key={p.id} className="nx-card-hover">
              <td>
                <span className="nx-ptable__name">
                  <Monogram name={p.name} color={projectColor(p.id)} size={34} radius={10} />
                  <Link to={`/projeler/${p.id}`}>{p.name}</Link>
                </span>
              </td>
              <td><VisibilityBadge visibility={p.visibility} /></td>
              <td>{p.myRole && <RoleBadge role={p.myRole} name={p.roleNames?.[0]} />}</td>
              <td className="nx-ptable__muted">
                {p.noteCount != null ? <><span className="nx-nums">{p.noteCount}</span> not · <span className="nx-nums">{p.fileCount ?? 0}</span> dosya</> : '—'}
              </td>
              <td className="nx-ptable__muted">{formatRelative(p.updatedAt)} · {p.ownerDisplayName}</td>
              <td className="nx-ptable__act"><ProjectMenu project={p} onDelete={() => onDelete(p)} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function CreateProjectModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const navigate = useNavigate()

  const form = useForm({
    initialValues: { name: '', description: '', visibility: 'PRIVATE' as Visibility },
    validate: { name: (v) => (v.trim() ? null : tr.common.required) },
  })

  const create = useMutation({
    mutationFn: () =>
      projectApi.create({
        name: form.values.name.trim(),
        description: form.values.description.trim() || undefined,
        visibility: form.values.visibility,
      }),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ['projects'] })
      qc.invalidateQueries({ queryKey: ['projects-count'] })
      qc.invalidateQueries({ queryKey: ['home'] })
      form.reset()
      onClose()
      navigate(`/projeler/${p.id}`)
    },
    onError: (e) => notifications.show({ color: 'red', message: errorMessage(e) }),
  })

  const vis = form.values.visibility
  return (
    <Modal opened={opened} onClose={onClose} title={tr.project.newProject} size={540}
      footer={<>
        <Button variant="secondary" onClick={onClose}>{tr.common.cancel}</Button>
        <Button type="submit" form="create-project" loading={create.isPending}>{tr.common.create}</Button>
      </>}>
      <form id="create-project" onSubmit={form.onSubmit(() => create.mutate())} className="nx-create-project">
        <TextField label={tr.project.name} placeholder="Örn. Şubat Raporları" size="lg" autoFocus maxLength={200}
          {...form.getInputProps('name')} />
        <Textarea label={tr.project.description} placeholder="Bu proje ne hakkında?" autosize minRows={3} maxLength={2000}
          {...form.getInputProps('description')} />
        <fieldset className="nx-create-project__vis">
          <legend>{tr.project.visibility}</legend>
          <div role="radiogroup" aria-label={tr.project.visibility} className="nx-create-project__opts">
            <RadioCard checked={vis === 'PRIVATE'} onSelect={() => form.setFieldValue('visibility', 'PRIVATE')}
              icon={<IconLock size={17} stroke={1.8} />} title={tr.project.private} description={tr.project.privateHint} />
            <RadioCard checked={vis === 'SHARED'} onSelect={() => form.setFieldValue('visibility', 'SHARED')}
              icon={<IconUsers size={17} stroke={1.8} />} title={tr.project.shared} description={tr.project.sharedHint} />
          </div>
        </fieldset>
      </form>
    </Modal>
  )
}
